import SlideApi from '@/api/slide'
import * as Sentry from '@sentry/vue'
import { $http } from 'vue-resource'
import { decryptAndParse } from '@/utils/decrypt.util'
import kvSyncSubscriber from '@/mqtt/kvSync'

const getSlidesAction = async ({ commit, state }) => {
  const { presentation } = state

  const slides = await SlideApi.getSlides(presentation.id)

  commit('setSlides', slides)

  return slides
}

const setSlidesListAction = ({ commit }, payload) => {
  commit('setSlidesList', payload)
}

const setSlideIndexAction = ({ commit }, payload) => {
  const { index } = payload
  if (index === -1) {
    return
  }
  commit('setSlideIndex', index)
}

const setMaxLookbackSlideIndexAction = ({ commit }, payload) => {
  commit('setMaxLookbackSlideIndex', payload)
}

const setSlideV2Action = ({ commit }, payload) => {
  commit('setSlideV2', payload)
}

const compareObjectsSlide = (oldSlide, newSlide) => {
  // So sánh các key chính
  const keysToCompare = [
    'id',
    'title',
    'createdAt',
    'type',
    'limitChoice',
    'maxPoint',
    'minPoint',
    'multipleChoice',
    'order',
    'stopSubmission',
    'timeToAnswer',
    'slideType',
    'resetTimeStamp',
    'quizStatus',
    'questionIndex',
    'questionCount',
  ]

  if (keysToCompare.some((key) => oldSlide[key] !== newSlide[key])) {
    return true
  }

  /**
   * Remove comparing quizTimestamp since slideV2 does not implement quiz stage
   */
  // // So sánh quizTimestamp (phải cùng độ dài và từng phần tử giống nhau)
  // if (!Array.isArray(oldSlide.quizTimestamp) || !Array.isArray(newSlide.quizTimestamp)) {
  //   return true
  // }

  // if (oldSlide.quizTimestamp.length !== newSlide.quizTimestamp.length) {
  //   return true
  // }

  // if (oldSlide.quizTimestamp.some((timestamp, index) => timestamp !== newSlide.quizTimestamp[index])) {
  //   return true
  // }

  // Kiểm tra nếu SlideOptions không tồn tại trong một trong hai object
  if (!Array.isArray(oldSlide.SlideOptions) || !Array.isArray(newSlide.SlideOptions)) {
    return true
  }

  // So sánh độ dài SlideOptions
  if (oldSlide.SlideOptions.length !== newSlide.SlideOptions.length) {
    return true
  }

  // So sánh từng phần tử trong SlideOptions
  return oldSlide.SlideOptions.some((slide1, i) => {
    const slide2 = newSlide.SlideOptions[i]
    return (
      slide1.id !== slide2.id ||
      slide1.title !== slide2.title ||
      slide1.correct !== slide2.correct
    )
  })
}

const compareDataSlideV2Action = ({ state }) => {
  if (compareObjectsSlide(state.slide, state.slideV2)) {
    Sentry.captureException(new Error('New slide data not correct'), {
      extra: {
        slide: state.slide,
        slideOptions: state.slide.SlideOptions,
        slideV2: state.slideV2,
        slideV2Options: state.slideV2.SlideOptions,
      },
    })
  }
}

const getNextSlideAction = async ({ rootGetters, dispatch }, slideId) => {
  const canUseEncryptedSlideData = rootGetters['featureToggles/canUseEncryptedSlideData']
  const canUseSlidePlugin = rootGetters['featureToggles/canUseSlidePlugin']
  const [slideResp, attributes] = await Promise.all([
    SlideApi.getNextSlide(slideId, canUseEncryptedSlideData),
    canUseSlidePlugin ? dispatch('getSlideAttributesBySlideAction', slideId) : Promise.resolve(undefined),
  ])

  if (attributes) {
    return {
      ...slideResp,
      body: {
        ...slideResp.body,
        attributes,
      },
    }
  }
  return slideResp
}

const getSlideFromCDNUrlAction = async ({ rootGetters, dispatch }, { url, presentationId }) => {
  const canUseSlidePlugin = rootGetters['featureToggles/canUseSlidePlugin']
  const res = await $http.get(url)
  const body = decryptAndParse(res.bodyText, presentationId)
  if (canUseSlidePlugin) {
    // get attributes for slide
    body.attributes = await dispatch('getSlideAttributesBySlideAction', body.id)
  }
  return {
    ...res,
    body,
  }
}

const getSlideAttributesByPresentationAction = async ({ state, commit, rootGetters }) => {
  try {
    const { presentation } = state
    const canUseSlidePlugin = rootGetters['featureToggles/canUseSlidePlugin']
    const attributes = await SlideApi.getSlideAttributes({
      presentationId: presentation.id,
      ...(canUseSlidePlugin ? { resignImage: true } : {}),
    })
    const slideAttributesMap = attributes.reduce((obj, attr) => {
      obj[attr.slideId] = obj[attr.slideId] || {}
      obj[attr.slideId][attr.type] = attr.attributes
      return obj
    }, {})
    commit('setSlideAttributes', slideAttributesMap)
    return slideAttributesMap
  } catch (err) {
    Sentry.captureException(err)
    return {}
  }
}

const getSlideAttributesBySlideAction = async ({ commit, rootGetters }, slideId) => {
  try {
    const canUseSlidePlugin = rootGetters['featureToggles/canUseSlidePlugin']
    const attributes = await SlideApi.getSlideAttributes({
      slideIds: slideId,
      ...(canUseSlidePlugin ? { resignImage: true } : {}),
    })
    const attributeMap = (attributes ?? []).reduce((obj, attr) => {
      obj[attr.type] = attr.attributes
      return obj
    }, {})
    commit('setSlideAttributes', { [slideId]: attributeMap })
    return attributeMap
  } catch (error) {
    Sentry.captureException(error)
    return {}
  }
}

const getSlidesWithAttributesAction = async ({ dispatch }) => {
  // First get the slides
  const slides = await dispatch('getSlidesAction')

  // Get attributes for all slides
  const attributes = await dispatch('getSlideAttributesByPresentationAction')
  // Merge slides with their attributes
  return slides.map((slide) => {
    const slideAttributes = attributes[slide.id]
    return {
      ...slide,
      attributes: slideAttributes || null,
    }
  })
}

const getAttributeChangesTopic = ({ presentationId }) => {
  return `presentation-${presentationId}-slide-attributes/#`
}

const subscribeSlideAttributeChanges = async (
  { commit },
  { presentationId },
) => {
  if (!presentationId) return
  const callbackWrapper = (topic, message) => {
    // For reference: the topic is form in general-api
    // services-layer/slide-service
    const regex = /slide-(\d+)-(.+)$/
    const match = topic.match(regex)
    if (!match) return
    const [, slideId, attributeKey] = match
    const attributeValue = JSON.parse(message)
    const attributeUpdated = { [attributeKey]: attributeValue }
    commit('setSlideAttributes', { [slideId]: attributeUpdated })
  }
  const key = getAttributeChangesTopic({ presentationId })
  kvSyncSubscriber.subscribeKey(key, callbackWrapper)
}

const unsubscribeSlideAttributeChanges = (_, { presentationId }) => {
  kvSyncSubscriber.unsubscribeKey(getAttributeChangesTopic({ presentationId }))
}

export default {
  getSlidesAction,
  setSlidesListAction,
  setSlideIndexAction,
  setMaxLookbackSlideIndexAction,
  setSlideV2Action,
  compareDataSlideV2Action,
  getNextSlideAction,
  getSlideFromCDNUrlAction,
  getSlideAttributesByPresentationAction,
  getSlidesWithAttributesAction,
  getSlideAttributesBySlideAction,
  subscribeSlideAttributeChanges,
  unsubscribeSlideAttributeChanges,
}
